import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Buildings,
  CaretDown,
  CaretRight,
  ArrowClockwise,
  Receipt,
} from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Skeleton } from "components/ui/skeleton";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { formatCurrency } from "shared/utils/currency";

interface BuildingInvoiceImportMetric {
  buildingId: string;
  buildingName: string;
  invoiceCount: number;
  totalInvoiceAmount: number;
  supplierNamesDisplay?: string;
}

interface BuildingsWithPendingInvoicesProps {
  /**
   * The currently active building filter (read from URL search params by the
   * host page). When a row matches this id it is highlighted so the user sees
   * which set of documents is being shown below.
   */
  activeBuildingId?: string;
  /**
   * Fires after a building row is clicked and the URL filter has been updated.
   * Hosts use this to e.g. switch the active tab so the user instantly sees
   * the filtered documents.
   */
  onBuildingSelected?: (buildingId: string) => void;
  /**
   * Fires whenever the buildings-with-pending-invoices list is (re)loaded with
   * the TOTAL number of invoices to import (summed across all buildings), so
   * the host page can show it as a count badge (e.g. in a tab trigger).
   */
  onCountChange?: (invoiceCount: number) => void;
  /**
   * Bump this number to make the list refetch its (cached) metrics — used after
   * an import or a global refresh so the imported invoice drops off the list.
   */
  refreshToken?: number;
  /** True while a global analytics rebuild is running (drives the spinner). */
  isRefreshing?: boolean;
  /**
   * Global "Vernieuwen" handler owned by the host page. When provided, the
   * refresh button rebuilds the whole analytics cache (per-building list,
   * zonder-kostenplaats list AND the KPIs) instead of only this list.
   */
  onRefresh?: () => void;
}

const formatDateTime = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
};

/**
 * BuildingsWithPendingInvoices
 *
 * Displays the list of buildings that have renovation invoices ready to be
 * imported. Clicking a row sets `?building=<id>` on the current /renovation/documents
 * URL so the documents table below auto-filters on that building.
 *
 * The section is collapsible — it starts open when there is data, but the user
 * can fold it away to free up vertical space.
 */
const BuildingsWithPendingInvoicesComponent = ({
  activeBuildingId,
  onBuildingSelected,
  onCountChange,
  refreshToken,
  isRefreshing: isRefreshingProp,
  onRefresh,
}: BuildingsWithPendingInvoicesProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [metrics, setMetrics] = useState<BuildingInvoiceImportMetric[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Spinner reflects either a host-driven global refresh or a local one.
  const refreshing = isRefreshingProp ?? isRefreshing;

  const fetchMetrics = useCallback(
    async (refresh: boolean = false) => {
      if (!session) return;
      if (refresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      try {
        const endpoint = refresh
          ? `${API_URL}/renovation/refresh-analytics`
          : `${API_URL}/renovation/invoice-import-metrics`;
        const response = await fetchWithAuth(endpoint, { method: "GET" }, session);
        if (!response.ok) {
          throw new Error(
            `Failed to load invoice import metrics: ${response.status} ${response.statusText}`,
          );
        }
        const payload = await response.json();
        const data = Array.isArray(payload?.data) ? payload.data : [];
        setMetrics(data);
        setLastUpdated(payload?.lastUpdated ?? null);
        // Report the total number of invoices to import (summed across all
        // buildings), NOT the number of buildings — that is what the tab badge
        // should reflect.
        const totalInvoiceCount = data.reduce(
          (sum: number, m: BuildingInvoiceImportMetric) =>
            sum + (m.invoiceCount ?? 0),
          0,
        );
        onCountChange?.(totalInvoiceCount);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Onbekende fout bij ophalen van facturen om te importeren";
        console.error("BuildingsWithPendingInvoices fetch error:", err);
        setError(message);
        if (refresh) {
          toast.error("Fout bij vernieuwen van de gegevens");
        }
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [session, onCountChange],
  );

  useEffect(() => {
    if (session) {
      fetchMetrics(false);
    }
  }, [session, fetchMetrics]);

  // Refetch the (cached) metrics when the host bumps the refresh token — e.g.
  // after an import or a global "Vernieuwen". A plain GET; the rebuild itself
  // (if any) already happened on the host side.
  useEffect(() => {
    if (session && refreshToken) {
      fetchMetrics(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleBuildingClick = useCallback(
    (buildingId: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("building", buildingId);
      newParams.delete("buildingId");
      navigate(`/renovation/documents?${newParams.toString()}`);
      onBuildingSelected?.(buildingId);
    },
    [navigate, searchParams, onBuildingSelected],
  );

  const totals = useMemo(() => {
    let invoiceCount = 0;
    let totalAmount = 0;
    for (const m of metrics) {
      invoiceCount += m.invoiceCount;
      totalAmount += m.totalInvoiceAmount;
    }
    return { buildingCount: metrics.length, invoiceCount, totalAmount };
  }, [metrics]);

  const lastUpdatedDisplay = formatDateTime(lastUpdated);

  return (
    <section className="px-6 pt-3">
      <div className="rounded-lg border border-neutral-200 bg-white">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3">
          <button
            type="button"
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="hover:text-moss-primary flex items-center gap-2 text-left"
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <CaretRight size={16} weight="bold" />
            ) : (
              <CaretDown size={16} weight="bold" />
            )}
            <Receipt
              size={20}
              weight="duotone"
              className="text-moss-primary"
            />
            <h2 className="text-base font-semibold text-neutral-primary">
              Facturen te importeren per gebouw
            </h2>
            {!isLoading && metrics.length > 0 && (
              <span className="bg-moss-primary/10 text-moss-primary rounded-full px-2 py-0.5 text-xs font-semibold">
                {totals.buildingCount} gebouwen · {totals.invoiceCount}{" "}
                facturen · {formatCurrency(totals.totalAmount)}
              </span>
            )}
          </button>
          <div className="flex items-center gap-3 text-xs text-neutral-secondary-mid">
            {lastUpdatedDisplay && (
              <span>Laatst bijgewerkt: {lastUpdatedDisplay}</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (onRefresh) onRefresh();
                else fetchMetrics(true);
              }}
              disabled={refreshing || isLoading}
            >
              <ArrowClockwise
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              Vernieuwen
            </Button>
          </div>
        </div>

        {/* Body */}
        {!isCollapsed && (
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : error ? (
              <div className="p-4 text-sm text-red-600">{error}</div>
            ) : metrics.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-neutral-secondary-mid">
                <Buildings size={28} weight="duotone" className="text-neutral-300" />
                <span>
                  Geen openstaande facturen om te importeren. Alles is up-to-date.
                </span>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[28%]">Gebouw</TableHead>
                    <TableHead className="w-[14%]">Aantal facturen</TableHead>
                    <TableHead className="w-[34%]">Leveranciers</TableHead>
                    <TableHead className="w-[16%] text-right">
                      Totaal bedrag
                    </TableHead>
                    <TableHead className="w-[8%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((metric) => {
                    const isActive = activeBuildingId === metric.buildingId;
                    return (
                      <TableRow
                        key={metric.buildingId}
                        onClick={() => handleBuildingClick(metric.buildingId)}
                        className={`cursor-pointer ${
                          isActive
                            ? "bg-moss-primary/10 hover:bg-moss-primary/15"
                            : "hover:bg-neutral-50"
                        }`}
                        title="Klik om dit gebouw te selecteren voor import"
                      >
                        <TableCell className="font-medium text-neutral-primary">
                          <div className="flex items-center gap-2">
                            <Buildings
                              size={16}
                              weight="duotone"
                              className="text-moss-primary shrink-0"
                            />
                            <span className="truncate">
                              {metric.buildingName}
                            </span>
                            {isActive && (
                              <span className="bg-moss-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                                Actief filter
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {metric.invoiceCount}
                        </TableCell>
                        <TableCell className="text-neutral-secondary-mid">
                          <span
                            className="block truncate"
                            title={metric.supplierNamesDisplay || ""}
                          >
                            {metric.supplierNamesDisplay || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatCurrency(metric.totalInvoiceAmount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-moss-primary text-xs font-semibold hover:underline">
                            {isActive ? "Geselecteerd" : "Selecteer →"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export const BuildingsWithPendingInvoices = memo(
  BuildingsWithPendingInvoicesComponent,
);
